// =============================================================================
// MeuDriver — LessonPackagesCard
// Lista de pacotes disponíveis para compra com destaque do melhor custo-benefício
// =============================================================================

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Package, ShoppingCart, ChevronRight, Loader2, Check } from 'lucide-react'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useLessonPackages, usePurchasePackage } from '@/hooks/usePackages'
import { formatCurrency, cn } from '@/lib/utils'

interface PackageTileProps {
  pkg: {
    id: string
    name: string
    lessons_count: number
    price: number
    price_per_lesson: number
    discount_percent: number
  }
  isBest: boolean
  onBuy: (packageId: string) => void
  isPending: boolean
  selectedId: string | null
}

function PackageTile({ pkg, isBest, onBuy, isPending, selectedId }: PackageTileProps) {
  const isThisLoading = isPending && selectedId === pkg.id

  return (
    <div
      className={cn(
        'relative rounded-2xl border-2 p-4 flex flex-col gap-2 transition-shadow',
        isBest
          ? 'border-brand-400 bg-gradient-to-br from-brand-50 to-teal-50 shadow-md'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm',
      )}
    >
      {isBest && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-600 px-3 py-0.5 text-[11px] font-bold text-white shadow">
          Mais popular
        </span>
      )}

      <div className="flex items-center justify-between">
        <p className="font-bold text-gray-900">{pkg.lessons_count} aulas</p>
        <span className={cn(
          'rounded-full px-2 py-0.5 text-[11px] font-bold',
          isBest ? 'bg-brand-600 text-white' : 'bg-green-100 text-green-700',
        )}>
          -{pkg.discount_percent}%
        </span>
      </div>

      <div>
        <p className="text-2xl font-extrabold text-gray-900">{formatCurrency(pkg.price)}</p>
        <p className="text-xs text-muted-foreground">
          {formatCurrency(pkg.price_per_lesson)}/aula
        </p>
      </div>

      <Button
        size="sm"
        onClick={() => onBuy(pkg.id)}
        disabled={isPending}
        className={cn('mt-1 gap-1.5', isBest ? 'bg-brand-600 hover:bg-brand-700' : '')}
        data-testid={`buy-package-${pkg.lessons_count}`}
      >
        {isThisLoading
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : <ShoppingCart className="h-3.5 w-3.5" />}
        {isThisLoading ? 'Aguarde...' : 'Comprar'}
      </Button>
    </div>
  )
}

export function LessonPackagesCard() {
  const { data: packages, isLoading } = useLessonPackages()
  const { mutate: purchasePackage, isPending } = usePurchasePackage()
  const [selectedId, setSelectedId] = useState<string | null>(null)

  function handleBuy(packageId: string) {
    setSelectedId(packageId)
    purchasePackage({ package_id: packageId })
  }

  // O pacote com melhor custo-benefício é o maior (mais desconto)
  const bestPkgId = packages?.reduce((best, p) =>
    p.discount_percent > best.discount_percent ? p : best,
    packages[0],
  )?.id

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-2xl border border-border/60 bg-white shadow-soft overflow-hidden"
    >
      <div className="px-5 pt-5 pb-4 border-b border-border/50 flex items-center justify-between">
        <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
          <Package className="h-4 w-4 text-brand-600" />
          Pacotes de aulas
        </h2>
        <Link
          to="/aluno/pacotes"
          className="flex items-center gap-1 text-sm text-brand-600 font-semibold hover:text-brand-700 transition-colors"
        >
          Ver todos
          <ChevronRight className="h-3 w-3" />
        </Link>
      </div>

      <div className="px-5 py-4">
        <p className="text-sm text-muted-foreground mb-4">
          Compre um pacote e economize até <span className="font-bold text-brand-600">20%</span>. As aulas valem 1 ano.
        </p>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Skeleton className="h-36 rounded-2xl" />
            <Skeleton className="h-36 rounded-2xl" />
            <Skeleton className="h-36 rounded-2xl" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
            {(packages ?? []).map((pkg) => (
              <PackageTile
                key={pkg.id}
                pkg={pkg as any}
                isBest={pkg.id === bestPkgId}
                onBuy={handleBuy}
                isPending={isPending}
                selectedId={selectedId}
              />
            ))}
          </div>
        )}

        <div className="mt-4 flex items-start gap-2 text-xs text-muted-foreground">
          <Check className="h-3.5 w-3.5 shrink-0 text-green-500 mt-0.5" />
          Use as aulas com qualquer instrutor da plataforma
        </div>
      </div>
    </motion.section>
  )
}
